import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map } from 'rxjs/operators';
import { PeopleSdk } from 'lib-klab-sdks';
import { AppEventsService } from '../../events/products-events.service';
import { CoreUIToastDirection, CoreUIToastV2Service } from '../../services/toast.service';
import { EmployeesPort } from '../../../app/ports/people/employees.port';
import { CrudMockAction } from '../../config/crud-mock-action.type';
import { EnvMicroPeople } from '../../config/env.micro.people';
import { PEOPLE_ENV } from '../../config/micros-env.tokens';
import { employeesSdkMock } from '../../mocks/people/employees.mock';

type EmployeesMockAction = CrudMockAction | 'getWorkDaysById';

@Injectable({ providedIn: 'root' })
export class EmployeesPortAdapter implements EmployeesPort {
  constructor(
    private employeesApi: PeopleSdk.EmployeesService,
    private appEvents: AppEventsService,
    @Optional() private toast: CoreUIToastV2Service,
    @Inject(PEOPLE_ENV) private env: EnvMicroPeople,
  ) { }

  getAllEmployees(
    page?: number,
    size?: number,
    locationId?: number,
    departmentId?: number,
    roleId?: number,
    silent = false,
    status?: PeopleSdk.StatusEnum,
  ): Observable<PeopleSdk.PagedEmployeeListResponse> {
    this.appEvents.loaderVisiblePublish(true, 'people-employees-list');

    const source$ = this.shouldMock('getAll')
      ? of(employeesSdkMock.getAllEmployees(page, size, locationId, departmentId, roleId)).pipe(delay(360))
      : this.employeesApi.getAllEmployees(page, size, locationId, departmentId, roleId, status);

    return source$.pipe(
      map((resp) => resp),
      catchError((err) => {
        if (!silent) this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-employees-list')),
    );
  }

  getEmployeeById(employeeId: number): Observable<PeopleSdk.EmployeeResponse> {
    this.appEvents.loaderVisiblePublish(true, 'people-employee-getById');

    const source$ = this.shouldMock('getById')
      ? of(employeesSdkMock.getEmployeeById(employeeId)).pipe(delay(360))
      : this.employeesApi.getEmployeeById(employeeId);

    return source$.pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-employee-getById')),
    );
  }

  createEmployee(payload: PeopleSdk.EmployeeCreateRequest): Observable<PeopleSdk.EmployeeResponse> {
    this.appEvents.loaderVisiblePublish(true, 'people-employee-create');

    const source$ = this.shouldMock('create')
      ? of(employeesSdkMock.createEmployee(payload)).pipe(delay(360))
      : this.employeesApi.createEmployee(payload);

    return source$.pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-employee-create')),
    );
  }

  updateEmployee(employeeId: number, payload: PeopleSdk.EmployeeUpdateRequest): Observable<void> {
    this.appEvents.loaderVisiblePublish(true, 'people-employee-update');

    const source$ = this.shouldMock('update')
      ? of(employeesSdkMock.updateEmployee(employeeId, payload)).pipe(delay(360))
      : this.employeesApi.updateEmployee(employeeId, payload);

    return source$.pipe(
      map(() => void 0),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-employee-update')),
    );
  }

  deleteEmployeeById(employeeId: number): Observable<void> {
    this.appEvents.loaderVisiblePublish(true, 'people-employee-delete');

    const source$ = this.shouldMock('delete')
      ? of(employeesSdkMock.deleteEmployeeById(employeeId)).pipe(delay(360))
      : this.employeesApi.deleteEmployeeById(employeeId);

    return source$.pipe(
      map(() => void 0),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-employee-delete')),
    );
  }

  reinstateEmployeeById(employeeId: number): Observable<void> {
    this.appEvents.loaderVisiblePublish(true, 'people-employee-reinstate');

    return this.employeesApi.reinstateEmployeeById(employeeId).pipe(
      map(() => void 0),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-employee-reinstate')),
    );
  }

  getEmployeeWorkDaysById(
    employeeId: number,
  ): Observable<PeopleSdk.EmployeeWorkDayInternalResponse> {
    this.appEvents.loaderVisiblePublish(true, 'people-employee-workdays');

    const source$ = this.shouldMock('getWorkDaysById')
      ? of(employeesSdkMock.getEmployeeWorkDaysById(employeeId)).pipe(delay(360))
      : this.employeesApi.getEmployeeWorkDaysById(employeeId);

    return source$.pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-employee-workdays')),
    );
  }

  exportEmployees(payload: PeopleSdk.EmployeeExportRequest): Observable<Blob> {
    this.appEvents.loaderVisiblePublish(true, 'people-employees-export');

    return this.employeesApi.exportEmployees(payload).pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-employees-export')),
    );
  }

  exportScheduleAssignments(payload: PeopleSdk.EmployeeExportRequest): Observable<Blob> {
    this.appEvents.loaderVisiblePublish(true, 'people-schedule-assignment-export');

    return this.employeesApi.exportScheduleAssignments(payload).pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() =>
        this.appEvents.loaderVisiblePublish(false, 'people-schedule-assignment-export'),
      ),
    );
  }

  exportEmployeeDetail(employeeId: number, payload: { format: string }): Observable<Blob> {
    this.appEvents.loaderVisiblePublish(true, 'people-employee-detail-export');

    return this.employeesApi.exportEmployeeDetail(employeeId, payload).pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() =>
        this.appEvents.loaderVisiblePublish(false, 'people-employee-detail-export'),
      ),
    );
  }

  private shouldMock(action: EmployeesMockAction): boolean {
    return Boolean(this.env?.useMocks || this.env?.mock?.employees?.[action]);
  }

  private showErrorToast(err: any) {
    if (!this.toast) return;

    const status = err?.status;
    const body = err?.error;
    const description = typeof body?.description === 'string' ? body.description : '';

    const mapMsg: Record<number, string> = {
      400: 'Solicitud inválida',
      401: 'No autorizado',
      404: 'No encontrado',
      409: 'Este correo electrónico ya está en uso.',
      429: 'Demasiadas solicitudes',
      500: 'Error interno del servidor',
    };

    this.toast.show({
      data: {
        title: 'Error',
        msg: mapMsg[status] || description || 'Error inesperado',
        direction: CoreUIToastDirection.topCenter,
        autoClose: true,
      },
    });
  }
}
